import { useEffect, useRef, type RefObject } from 'react';
import { ArdoqId } from '@ardoq/api-types';
import type { MetaInfo } from 'streams/views/mainContent/types';
import { isDiscontinuedView } from 'viewDeprecation/discontinuedSlides';
import { Icon, IconName } from '@ardoq/icons';
import { Stack } from '@ardoq/layout';
import { dispatchAction } from '@ardoq/rxbeach';
import { t, type I18nLocale } from '@ardoq/i18n';
import { Popover2025, type HTMLElementWithAnchor } from '@ardoq/popovers';
import { PRESENTATION_CHROME_CLASS } from 'presentation/chrome';
import { selectSlide } from './actions';
import { Slide } from './types';
import {
  SLIDE_OVERVIEW_PANEL_ID,
  SlideOverviewItemButton,
  SlideOverviewItemNumber,
  SlideOverviewItemTitle,
  SlideOverviewPanel,
} from './atoms';

type SlideOverviewProps = {
  slides: Slide[];
  selectedSlideId: ArdoqId | null;
  anchorRef: RefObject<HTMLElementWithAnchor | null>;
  onClose: VoidFunction;
  deprecatedViews: MetaInfo[];
  i18nLocale: I18nLocale;
};

export const SlideOverview = ({
  slides,
  selectedSlideId,
  anchorRef,
  onClose,
  deprecatedViews,
  i18nLocale,
}: SlideOverviewProps) => {
  const selectedItemRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    selectedItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedSlideId]);

  return (
    <Popover2025
      isOpen
      onClose={onClose}
      id={SLIDE_OVERVIEW_PANEL_ID}
      anchorRef={anchorRef}
      placement={{ side: 'top', overflow: 'right' }}
      forcePortal
      className={PRESENTATION_CHROME_CLASS}
      role="region"
      aria-label={t(
        i18nLocale,
        'presentationAdmin:viewer.slideNavigator.slideOverview',
        'Slides'
      )}
    >
      <SlideOverviewPanel>
        <Stack as="ol" gap="xxsmall">
          {slides.map(({ id, title, viewId }, index) => {
            const isActive = id === selectedSlideId;
            return (
              <li key={id} ref={isActive ? selectedItemRef : undefined}>
                <SlideOverviewItemButton
                  $isActive={isActive}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => dispatchAction(selectSlide(id))}
                >
                  <SlideOverviewItemNumber
                    variant="text2"
                    color="textSubtle"
                    align="right"
                  >
                    {index + 1}
                  </SlideOverviewItemNumber>
                  <SlideOverviewItemTitle
                    variant="text2"
                    color={isActive ? 'textModerate' : 'textDefault'}
                    align="left"
                    truncate={{ lines: 2 }}
                  >
                    {title}
                  </SlideOverviewItemTitle>
                  {isDiscontinuedView(deprecatedViews, viewId) && (
                    <Icon
                      iconName={IconName.WARNING}
                      data-tooltip-text={t(
                        i18nLocale,
                        'presentationAdmin:viewer.slideNavigator.deprecatedSlideTooltip',
                        { title },
                        'Deprecated {{title}}'
                      )}
                    />
                  )}
                </SlideOverviewItemButton>
              </li>
            );
          })}
        </Stack>
      </SlideOverviewPanel>
    </Popover2025>
  );
};
